import type { TFunction } from 'i18next';
import type { Expertise, ListedExpertise, RemovalOutcome, Specialty } from 'api/referentialsApi';
import { ApiError } from 'utils/unwrapResponse';

/**
 * Wording for the confirmations of the referentials, shared so both screens say the same thing
 * about a withdrawal. The dialog is `src/components/ConfirmDeleteDialog`: warning tone when the
 * value survives (detached, deactivated), danger tone only for a real deletion (D78).
 */

export const deactivateCopy = (t: TFunction, name: string) => ({
  title: t('Deactivate {{name}}?', { name }),
  description: t('{{name}} stops being offered for selection.', { name }),
  // The reassurance goes in the framed block, not in the running text: it is the one thing an
  // ops has to register before clicking, and the design frames it for that reason.
  details: [
    {
      label: t('No deletion'),
      value: t(
        'The value stays visible on existing records and in the history, it is simply no longer offered for selection.'
      ),
    },
  ],
  confirmLabel: t('Deactivate'),
});

/**
 * Detaching a shared expertise is the case the mock-up performs silently: an ops tidying up
 * Installation would break Fabrication PLV without seeing it. The count of remaining specialties
 * is surfaced as a detail line, not buried.
 */
const detachCopy = (t: TFunction, expertise: Expertise) => ({
  title: t('Detach {{name}}?', { name: expertise.name }),
  description: t('The expertise leaves this specialty. It is not deleted and keeps its other attachments.'),
  confirmLabel: t('Detach'),
  details:
    expertise.specialty_ids.length > 1
      ? [
          {
            label: t('Shared'),
            value: t('This expertise is shared with {{count}} other specialty', {
              count: expertise.specialty_ids.length - 1,
            }),
            emphasis: true,
          },
        ]
      : [],
});

type ExpertiseRemoval = 'detach' | 'delete' | 'deactivate' | 'blocked';

/** What « Remove » will do, announced before the click (D78-c). */
export const expertiseRemoval = (expertise: ListedExpertise): ExpertiseRemoval => {
  if (expertise.specialty_ids.length > 1) return 'detach';
  if (expertise.usage_count === 0) return 'delete';
  return expertise.is_active ? 'deactivate' : 'blocked';
};

/** Deletable when nothing points at it nor at any expertise that would go with it. */
export const isSpecialtyDeletable = (specialty: Specialty): boolean =>
  specialty.usage_count === 0 &&
  specialty.expertises.every((expertise) => expertise.specialty_ids.length > 1 || expertise.usage_count === 0);

/** Why the trash of a specialty is blocked, or null when it can be deleted — shown on the row, not guessed. */
export const specialtyDeleteBlocker = (t: TFunction, specialty: Specialty): string | null => {
  if (isSpecialtyDeletable(specialty)) return null;
  if (specialty.usage_count > 0) {
    return t('Still carried by {{count}} records and tickets: it cannot be deleted', { count: specialty.usage_count });
  }
  return t('One of its own expertises is still in use: it cannot be deleted');
};

/** The value was removed elsewhere (another tab, another ops) between the listing and the click. */
export const isRemovedElsewhere = (error: unknown): boolean =>
  error instanceof ApiError && error.errorCode === 'REFERENTIAL_NOT_FOUND';

/** The toast says what the base did, which a race can make differ from what was announced. */
export const removalToast = (t: TFunction, outcome: RemovalOutcome, name: string) => {
  if (outcome === 'detached') return t('{{name}} detached', { name });
  if (outcome === 'deleted') return t('{{name}} deleted', { name });
  return t('{{name}} deactivated', { name });
};

const deleteCopy = (t: TFunction, name: string) => ({
  title: t('Delete {{name}}?', { name }),
  description: t('Nothing uses {{name}}: it is deleted for good. This cannot be undone, but the value can be created again.', {
    name,
  }),
  confirmLabel: t('Delete'),
  tone: 'danger' as const,
});

// 'blocked' never reaches a confirmation — its trash is disabled — so it shares the deactivation copy.
export const removeExpertiseCopy = (t: TFunction, expertise: ListedExpertise) => {
  const removal = expertiseRemoval(expertise);
  if (removal === 'detach') return { ...detachCopy(t, expertise), tone: 'warning' as const };
  if (removal === 'delete') return { ...deleteCopy(t, expertise.name), details: [] };
  return {
    ...deactivateCopy(t, expertise.name),
    description: t('{{name}} is still carried by {{count}} records and tickets: it is deactivated, not deleted.', {
      name: expertise.name,
      count: expertise.usage_count,
    }),
    details: [
      {
        label: t('No deletion'),
        value: t('It stays on the records and tickets that carry it and is no longer offered. It can be reactivated from this dialog.'),
      },
    ],
    tone: 'warning' as const,
  };
};

export const deleteSpecialtyCopy = (t: TFunction, specialty: Specialty) => ({
  ...deleteCopy(t, specialty.name),
  details:
    specialty.expertises.length > 0
      ? [
          {
            label: t('Expertises'),
            value: t('Its own expertises are deleted with it; the shared ones stay attached to their other specialties.'),
          },
        ]
      : [],
});
